import { useState } from 'react'
import {
  ACTIVE_VIDEO_STATUSES,
  type CreateVideoDraftRequest,
  type VideoGeneration,
  type VideoGenerationHistoryResponse,
  type VideoGenerationStatus
} from '@shared/index'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'

import { captureGenerationSession, trackGeneration } from '@/lib/generation-notifications'

import { api } from './api'

export const videoGenerationKeys = {
  all: ['video-generations'] as const,
  detail: (id: string) => [...videoGenerationKeys.all, 'detail', id] as const,
  history: (cursor?: string, status?: VideoGenerationStatus, limit = 20, creator = '') =>
    [...videoGenerationKeys.all, 'history', cursor ?? '', status ?? 'all', limit, creator] as const,
  historyPage: (page: number, status?: VideoGenerationStatus, limit = 20, creator = '') =>
    [...videoGenerationKeys.all, 'history-page', page, status ?? 'all', limit, creator] as const
}

function videoHistoryOptions(
  cursor: string | undefined,
  status: VideoGenerationStatus | undefined,
  limit: number,
  creator: string
) {
  return {
    queryKey: videoGenerationKeys.history(cursor, status, limit, creator),
    queryFn: () => api.getVideoGenerations({ limit, cursor, status, creator })
  }
}

function detailInterval(generation?: VideoGeneration): number | false {
  if (!generation || !ACTIVE_VIDEO_STATUSES.includes(generation.status)) return false
  const age = Date.now() - new Date(generation.submittedAt ?? generation.createdAt).getTime()
  if (age > 30 * 60_000) return 30_000
  if (generation.status === 'GENERATING') return 10_000
  return 5_000
}

function historyInterval(generations?: VideoGeneration[]): number | false {
  const intervals = generations?.map(detailInterval).filter((interval): interval is number => interval !== false) ?? []
  return intervals.length ? Math.min(...intervals) : false
}

export function useVideoGeneration(id?: string) {
  return useQuery({
    queryKey: videoGenerationKeys.detail(id ?? ''),
    queryFn: () => api.getVideoGeneration(id ?? ''),
    enabled: Boolean(id),
    refetchInterval: (query) => detailInterval(query.state.data)
  })
}

export function useVideoGenerationHistory(
  cursor?: string,
  status?: VideoGenerationStatus,
  limit = 20,
  enabled = true,
  creator = ''
) {
  return useQuery<VideoGenerationHistoryResponse>({
    ...videoHistoryOptions(cursor, status, limit, creator),
    enabled,
    refetchInterval: (query) => historyInterval(query.state.data?.generations)
  })
}

export function useVideoGenerationHistoryPage(page: number, status?: VideoGenerationStatus, creator = '') {
  const queryClient = useQueryClient()
  const resolution = useQuery({
    queryKey: videoGenerationKeys.historyPage(page, status, 20, creator),
    enabled: page > 1,
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      let cursor: string | undefined
      let previousCursor: string | undefined
      // ponytail: cold deep links read preceding pages; add server page lookup if this becomes expensive.
      for (let currentPage = 1; currentPage <= page; currentPage++) {
        signal.throwIfAborted()
        // Reuse a recent page, but re-read pages invalidated by a new or deleted video so cursors stay current.
        const result = await queryClient.fetchQuery({
          ...videoHistoryOptions(cursor, status, 20, creator),
          staleTime: 30_000
        })
        signal.throwIfAborted()
        if (!result.generations.length && !result.nextCursor && currentPage > 1) {
          return { cursor: previousCursor, page: currentPage - 1 }
        }
        if (currentPage === page || !result.nextCursor) return { cursor, page: currentPage }
        previousCursor = cursor
        cursor = result.nextCursor
      }
      throw new Error('Could not resolve history page')
    }
  })
  const resolved = page === 1 ? { cursor: undefined, page: 1 } : resolution.data
  const history = useVideoGenerationHistory(resolved?.cursor, status, 20, !!resolved, creator)
  const isResolving = page > 1 && resolution.isPending
  return {
    ...history,
    data: resolved ? history.data : undefined,
    page: resolved?.page ?? page,
    isLoading: isResolving || history.isLoading,
    isFetching: isResolving || resolution.isFetching || history.isFetching,
    isError: resolution.isError || history.isError,
    error: resolution.error ?? history.error,
    refetch: resolution.isError ? resolution.refetch : history.refetch
  }
}

export function useVideoInputPreviews(generationId: string | undefined, inputIds: string[]) {
  const [failedPreviews, setFailedPreviews] = useState<Set<string>>(() => new Set())
  const queries = useQueries({
    queries: inputIds.map((inputId) => ({
      queryKey: [...videoGenerationKeys.detail(generationId ?? ''), 'input', inputId],
      queryFn: () => api.getVideoMediaUrl(generationId ?? '', 'inline', inputId),
      enabled: Boolean(generationId),
      staleTime: 10 * 60_000,
      refetchInterval: 10 * 60_000,
      retry: false
    }))
  })
  return Object.fromEntries(
    queries.flatMap((query, index) => {
      const inputId = inputIds[index]
      if (!inputId) return []
      const key = `${generationId}:${inputId}`
      return [
        [
          inputId,
          {
            preview: query.data?.url,
            previewLoading: query.isPending,
            previewError:
              query.isError || failedPreviews.has(key)
                ? 'Preview unavailable. Retry the preview, or remove and replace this file.'
                : undefined,
            unavailable: query.error instanceof Error && 'status' in query.error && query.error.status === 404,
            onRetryPreview: () => {
              setFailedPreviews((current) => {
                const next = new Set(current)
                next.delete(key)
                return next
              })
              void query.refetch()
            },
            onPreviewError: () => setFailedPreviews((current) => new Set(current).add(key))
          }
        ] as const
      ]
    })
  )
}

export function usePrepareVideoGeneration() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ request, files }: { request: CreateVideoDraftRequest; files: Map<string, File> }) => {
      const draft = await api.createVideoDraft(request)
      await Promise.all(
        draft.uploads.map((upload) => {
          const file = files.get(upload.inputId)
          const input = request.inputs.find((candidate) => candidate.inputId === upload.inputId)
          if (!file || !input) throw new Error('Missing local video input')
          return api.uploadVideoInput(upload.uploadUrl, file, input)
        })
      )
      return draft
    },
    onSuccess: ({ generation }) => {
      queryClient.setQueryData(videoGenerationKeys.detail(generation.generationId), generation)
    }
  })
}

export function useSubmitVideoGeneration() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.submitVideoGeneration,
    onMutate: captureGenerationSession,
    onSuccess: ({ generation }, _, session) => {
      trackGeneration(
        {
          kind: 'video',
          id: generation.generationId,
          href: `/video-generation?generationId=${generation.generationId}`,
          label: 'Video generation'
        },
        session
      )
      queryClient.setQueryData(videoGenerationKeys.detail(generation.generationId), generation)
      void queryClient.invalidateQueries({ queryKey: videoGenerationKeys.all })
    }
  })
}

export function useDeleteVideoGeneration() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.deleteVideoGeneration,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: videoGenerationKeys.all })
  })
}
